/** Clave para entrar como administrador a NovaBerry (esta app).
 *
 *  Es aparte del PIN de acceso a NovaBerry POS (`src/data/pos.ts`,
 *  campos `pinHash`/`pinSalt`): ese PIN es para entrar a un local
 *  físico, este es para que no cualquiera que tenga el link pueda
 *  tocar el nombre del administrador y entrar con todos los permisos.
 *  Son cosas distintas, así que viven en columnas distintas —mezclarlas
 *  significaría que cambiar una cambia la otra sin que nadie lo pida.
 *
 *  El cálculo del hash es el mismo que el de POS (SHA-256 de
 *  `"novaberry:" + sal + ":" + clave"`) porque ya está probado y no hay
 *  ninguna razón para inventar uno nuevo, pero la sal y el hash se
 *  guardan en `claveSal`/`claveHash`, no en `pinSalt`/`pinHash`.
 *
 *  Las columnas están en `supabase/admin-clave.sql`.
 */

function nuevaSal(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(16))
  return [...bytes].map((b) => b.toString(16).padStart(2, '0')).join('')
}

async function hashearClave(clave: string, sal: string): Promise<string> {
  const datos = new TextEncoder().encode(`novaberry:${sal}:${clave}`)
  const buffer = await crypto.subtle.digest('SHA-256', datos)
  return [...new Uint8Array(buffer)].map((b) => b.toString(16).padStart(2, '0')).join('')
}

/** Calcula un par sal+hash nuevo para guardar como `claveSal`/`claveHash`
 *  de un administrador. No escribe nada solo: quien llama decide dónde
 *  guardarlo (ficha de usuario, script de una sola vez, etc.). */
export async function generarClaveAdmin(clave: string): Promise<{ hash: string; sal: string }> {
  const sal = nuevaSal()
  const hash = await hashearClave(clave, sal)
  return { hash, sal }
}

/** Compara lo que alguien tecleó contra el hash guardado. */
export async function verificarClaveAdmin(
  clave: string,
  hash: string,
  sal: string,
): Promise<boolean> {
  return (await hashearClave(clave, sal)) === hash
}
